// Shared popup-positioning helper for the four raw Fluent `Combobox`/`Dropdown`
// popups that open inside a Dialog: `EntityPicker` (this directory), the "Path
// to Point Click Care" `Dropdown` in `EditableInvoiceForm.tsx`, the entity-type
// `Dropdown` in `AssociatedEntitiesEditor.tsx`, and the freeform vehicle-field
// `Combobox` in `screens/TrainingScreen.tsx`. All four hit the same failure —
// `autoSize` collapsing the popup to one scrolling row and flipping it above
// the field — for the same reason (app/README.md's Fluent UI v9 gotcha list).
// A plain function, not a component: `react-refresh/only-export-components`
// refuses to share a file's fast-refresh boundary between a component and a
// runtime function export, which is why this lives beside `EntityPicker.tsx`
// rather than inside it.

import { makeStyles } from '@fluentui/react-components'

/**
 * `positioning` is carried UNCONDITIONALLY: it fixes the collapse whether the
 * popup ends up portaled at a resolved dialog host or, lacking one, inline —
 * measuring against the window and turning `autoSize` off is what the inline
 * fallback needs too, not only the portaled case. The host/inline choice is
 * the other, separate half, and it is mutually exclusive: a resolved host
 * mounts the popup at the dialog surface (inside the focus trap, above the
 * fields it would otherwise fight for stacking order with); no host falls
 * back to `inlinePopup`, which is the FALLBACK now, not the norm — most
 * callers resolve a host and never take this branch.
 */
export function popupPositionProps(host: HTMLElement | null | undefined) {
  return {
    ...(host ? { mountNode: host } : { inlinePopup: true as const }),
    positioning: {
      position: 'below' as const,
      align: 'start' as const,
      autoSize: false,
      flipBoundary: 'window' as const,
      overflowBoundary: 'window' as const,
    },
  }
}

const useListboxStyles = makeStyles({
  // Two failures this fixes for the three callers below, neither of which
  // styles its own popup the way `EntityPicker`'s `s.listbox` does:
  //
  // 1. STACKING. `usePositioningOptions` resolves `position: 'below'` (above)
  //    to `position: absolute`, and Fluent's `useListboxStyles` sets no
  //    z-index of its own. An explicit `mountNode` (what `popupPositionProps`
  //    passes whenever `host` resolves) disables `usePortal`'s OWN mount-node
  //    creation, and `usePortalMountNodeStyles` — the only place Fluent sets
  //    `zIndex: 1000000` — fires only for that self-supplied node. So a
  //    listbox mounted at a resolved dialog host lands in the z-auto layer of
  //    that dialog's stacking context: a sibling, not a superior, of anything
  //    else raised in the same dialog (a `PopoverSurface` with its own
  //    explicit z-index, a sticky action bar). `AssociatedEntitiesEditor`'s
  //    type `Dropdown` is the deterministic case — it opens inside a
  //    `PopoverSurface` at `zIndex: 10` (`entityPopSurface`,
  //    `EditableInvoiceForm.tsx`) mounted at the same dialog host, so without
  //    this it renders behind its own popover.
  //
  // 2. HEIGHT. `autoSize: false` (in `positioning` above) means Fluent writes
  //    no inline `max-height` — the fix for the collapse this file exists
  //    for, but it also means nothing bounds the popup's height any more.
  //    Before this shared helper existed all three callers ran `inlinePopup`
  //    with Fluent's default positioning (`autoSize` true, which DOES cap
  //    them), so this height was never missing until `autoSize` turned off.
  //    Worst case: `TrainingScreen`'s freeform vehicle-field suggestions are a
  //    Set over the entire fleet — `company_label` alone can run to several
  //    dozen rows, uncapped and unscrolled without this.
  popupListbox: {
    zIndex: 1000,
    maxHeight: '280px',
    overflowY: 'auto',
  },
})

/**
 * The listbox class the three non-`EntityPicker` callers of
 * `popupPositionProps` need — `EntityPicker` carries the same two rules on its
 * own `s.listbox` (plus the surface/legibility styling that one already
 * needed) and is unaffected by this. Call as a hook in the caller's render and
 * pass the result through `listbox={{ className }}` alongside
 * `{...popupPositionProps(host)}`.
 */
export function usePopupListboxClass(): string {
  return useListboxStyles().popupListbox
}
